import { useState, useCallback, useMemo } from "react";
import { BASE_URL } from "../../apiConfig";
import { useAuth } from "../../context/AuthProvider";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const VARIANT_TYPES = [
  { value: "color", label: "Color", icon: "🎨" },
  { value: "size", label: "Size", icon: "📏" },
  { value: "material", label: "Material", icon: "🧵" },
  { value: "other", label: "Other", icon: "⚙️" },
];

const PREDEFINED_COLORS = [
  "var(--color-primary-500)", "#3b82f6", "#ef4444", "#f59e0b",
  "#10b981", "#8b5cf6", "#ec4899", "#06b6d4",
  "#84cc16", "#f97316", "#6366f1", "#14b8a6",
  "#000000", "#ffffff", "#6b7280", "#1f2937",
  "#fbbf24", "#dc2626",
];

const isHexColor = (value) => /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value);

export default function CreateVariantForm() {
  const { token } = useAuth();
  
  const [formData, setFormData] = useState({
    name: "",
    value: "",
    type: "color",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    setSuccess(false);
    setErrorMessage("");
  }, [errors]);

  const handleColorSelect = useCallback((color) => {
    setFormData(prev => ({ ...prev, value: color }));
    if (errors.value) setErrors(prev => ({ ...prev, value: "" }));
    setSuccess(false);
    setErrorMessage("");
  }, [errors]);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Variant name is required";
    }
    if (!formData.value.trim()) {
      newErrors.value = "Variant value is required";
    } else if (formData.type === "color" && !isHexColor(formData.value)) {
      newErrors.value = "Please enter a valid hex color code (e.g., var(--color-primary-500))";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const response = await fetch(BASE_URL + "/api/variants", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        if (response.status === 422 && result.errors) {
          setErrors(result.errors);
        } else {
          setErrorMessage(result.message || "Failed to create variant. Please try again.");
        }
        throw new Error("Variant creation failed");
      }

      setSuccess(true);
      toast.success('ajout réussi !');
      setFormData({ name: "", value: "", type: "color" });
      setErrors({});
      setTimeout(() => setSuccess(false), 3000);

    } catch (error) {
      console.error("Error:", error);
      if (!errorMessage) {
        setErrorMessage("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const previewData = useMemo(() => {
    const selectedType = VARIANT_TYPES.find(type => type.value === formData.type);
    return {
      displayValue: formData.value || "No value",
      displayName: formData.name || "Variant Name",
      displayType: selectedType?.label?.toUpperCase() || "TYPE",
      isColorTypeAndValid: formData.type === "color" && isHexColor(formData.value),
    };
  }, [formData]);

  return (
    <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
      {success && (
        <div className="bg-[var(--color-primary-500)] text-white p-6 text-center">
          <div className="flex items-center justify-center mb-2">
            <svg className="h-8 w-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-lg font-semibold">Variant Created Successfully!</span>
          </div>
          <p className="text-[var(--color-primary-50)]">Your new variant has been added to the system.</p>
        </div>
      )}

      {errorMessage && (
        <div className="bg-red-500 text-xs text-white p-4 text-center">
          <p>{errorMessage}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-8">
        {/* Variant Type */}
        <div className="mb-8">
          <label className="block font-semibold text-gray-900 mb-4 text-sm">Variant Type *</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {VARIANT_TYPES.map((type) => (
              <label
                key={type.value}
                className={`flex items-center py-2 px-3 text-xs rounded-2xl cursor-pointer transition-all duration-200 ${
                  formData.type === type.value
                    ? "bg-[var(--color-primary-50)] border-2 border-[var(--color-primary-500)] text-[var(--color-primary-500)]"
                    : "bg-gray-50 border-2 border-gray-200 hover:border-[var(--color-primary-500)] text-gray-700"
                }`}
              >
                <input
                  type="radio"
                  name="type"
                  value={type.value}
                  checked={formData.type === type.value}
                  onChange={handleInputChange}
                  className="sr-only"
                />
                <span className="text-xl mr-3">{type.icon}</span>
                <span className="font-medium text-xs">{type.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Variant Name */}
        <div className="mb-8">
          <label htmlFor="name" className="block font-semibold text-sm text-gray-900 mb-3">Variant Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Enter variant name..."
            className={`w-full py-3 px-4 text-xs border-2 rounded-2xl outline-none transition-all duration-200 bg-gray-50 focus:bg-white ${
              errors.name
                ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
            }`}
          />
          {errors.name && <p className="mt-2 text-red-600 text-xs">{errors.name}</p>}
        </div>

        {/* Variant Value */}
        <div className="mb-8">
          <label htmlFor="value" className="block font-semibold text-gray-900 mb-3 text-sm">Variant Value *</label>
          {formData.type === "color" ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <input
                  type="text"
                  id="value"
                  name="value"
                  value={formData.value}
                  onChange={handleInputChange}
                  placeholder="var(--color-primary-500)"
                  className={`flex-1 py-3 px-4 text-xs border-2 rounded-2xl bg-gray-50 outline-none  focus:bg-white font-mono ${
                    errors.value
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                />
                {isHexColor(formData.value) && (
                  <div
                    className="w-12 h-12 rounded-2xl border-2 border-gray-200 shadow-sm"
                    style={{ backgroundColor: formData.value }}
                  />
                )}
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-3">Or choose from predefined colors:</p>
                <div className="grid grid-cols-6 md:grid-cols-9 gap-3">
                  {PREDEFINED_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => handleColorSelect(color)}
                      className={`w-10 h-10 rounded-xl border-2 transition-all duration-200 hover:scale-110 ${
                        formData.value === color
                          ? "border-[var(--color-primary-500)] ring-4 ring-[var(--color-primary-100)]"
                          : "border-gray-200"
                      }`}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <input
              type="text"
              id="value"
              name="value"
              value={formData.value}
              onChange={handleInputChange}
              placeholder={`Enter ${formData.type} value...`}
              className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl bg-gray-50 focus:bg-white ${
                errors.value
                  ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                  : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
              }`}
            />
          )}
          {errors.value && <p className="mt-2 text-red-600 text-xs">{errors.value}</p>}
        </div>

        {/* Actions */}
        <div className="flex sm:flex-row gap-4 pt-6 border-t border-gray-200">
          <Link href="/variants" className="block cursor-pointer text-center flex-1 py-3 px-4 cursor-pointer text-sm border-2 border-gray-200 text-gray-700 font-semibold rounded-2xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-200">
            <button
              type="button"
              disabled={isLoading}
              className='cursor-pointer'
            >
              Annuler  
            </button>
          </Link> 
          <button
            type="submit"
            disabled={isLoading}
            className={`flex-1 py-3 px-4 cursor-pointer text-sm font-semibold rounded-2xl ${
              isLoading
                ? "bg-gray-400 text-white cursor-not-allowed"
                : "bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] text-white shadow-lg hover:shadow-xl transform hover:scale-105"
            }`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center text-sm">
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Saving...
              </span>
            ) : (
              <span className="flex items-center justify-center text-sm">
                Save
                <svg className="h-4 w-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </span>
            )}
          </button>
        </div>

        {/* Preview */}
        {(formData.name || formData.value) && (
          <div className="mt-8 p-6 bg-gray-50 rounded-2xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
            <div className="flex items-center gap-4">
              {previewData.isColorTypeAndValid ? (
                <div
                  className="w-12 h-12 rounded-full border-2 border-gray-200 shadow-sm"
                  style={{ backgroundColor: previewData.displayValue }}
                />
              ) : (
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                  <span className="text-gray-500 text-xs font-medium">
                    {previewData.displayValue ? String(previewData.displayValue).slice(0, 2).toUpperCase() : "??"}
                  </span>
                </div>
              )}
              <div>
                <div className="font-semibold text-gray-900">{previewData.displayName}</div>
                <div className="text-xs text-gray-400 mt-1">
                  {previewData.displayType}: {previewData.displayValue}
                </div>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
